// src/components/FeedReactions.jsx — custom Toppers reactions for the News Feed.
// Deliberately NOT emoji: each reaction is a hand-drawn SVG so it matches the
// app's icon language and renders identically on every device.
import { useState, useRef, useEffect } from 'react';
import api from '../utils/api';

const S = { fill: 'none', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };

const IcLike = ({ size = 18, filled }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" style={S}>
    <path d="M7 22V11l4.5-8.2c1.2.1 2 1 2 2.2V9h5.1a2 2 0 0 1 2 2.4l-1.4 7a2 2 0 0 1-2 1.6H7z" />
    <path d="M7 11H3.6v11H7z" />
  </svg>
);
const IcLove = ({ size = 18, filled }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" style={S}>
    <path d="M20.8 5.1a5.4 5.4 0 0 0-7.7 0l-1.1 1-1-1a5.4 5.4 0 1 0-7.7 7.6l1 1 7.7 7.6 7.8-7.6 1-1a5.4 5.4 0 0 0 0-7.6z" />
  </svg>
);
const IcInsight = ({ size = 18, filled }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" style={S}>
    <path d="M9 18h6" /><path d="M10 22h4" />
    <path d="M12 2a7 7 0 0 0-4 12.7c.6.5 1 1.3 1 2.1v.2h6v-.2c0-.8.4-1.6 1-2.1A7 7 0 0 0 12 2z" />
  </svg>
);
const IcCelebrate = ({ size = 18, filled }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" style={S}>
    <path d="M8 21h8" /><path d="M12 17v4" />
    <path d="M7 4h10v4.5a5 5 0 0 1-10 0z" />
    <path d="M17 5h3v1.5a3 3 0 0 1-3 3" fill="none" /><path d="M7 5H4v1.5a3 3 0 0 0 3 3" fill="none" />
  </svg>
);
const IcBoost = ({ size = 18, filled }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" style={S}>
    <path d="M5 21h14" /><path d="M12 18V6" /><path d="M6.5 11.5 12 6l5.5 5.5" />
  </svg>
);

// Order defines the picker layout, left → right.
export const REACTIONS = [
  { type: 'like',       label: 'Like',       color: '#2563eb', Icon: IcLike },
  { type: 'love',       label: 'Love',       color: '#e53e3e', Icon: IcLove },
  { type: 'insightful', label: 'Insightful', color: '#d97706', Icon: IcInsight },
  { type: 'celebrate',  label: 'Celebrate',  color: '#16a34a', Icon: IcCelebrate },
  { type: 'support',    label: 'Support',    color: '#7c3aed', Icon: IcBoost },
];
export const RX = Object.fromEntries(REACTIONS.map(r => [r.type, r]));

// Reaction button + long-press/hover picker. Optimistic; reverts on error.
export function ReactionButton({ postId, myReaction, reactionCounts, total, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const current = myReaction ? RX[myReaction] : null;
  const Icon = current?.Icon || IcLike;
  const color = current?.color || 'var(--txt-l)';

  const react = async (type) => {
    setOpen(false);
    const wasSame = myReaction === type;
    const nextType = wasSame ? null : type;
    // Optimistic count update.
    const nextCounts = { ...(reactionCounts || {}) };
    if (myReaction) nextCounts[myReaction] = Math.max(0, (nextCounts[myReaction] || 1) - 1);
    if (nextType)   nextCounts[nextType]   = (nextCounts[nextType] || 0) + 1;
    const nextTotal = Object.values(nextCounts).reduce((a, b) => a + b, 0);
    onChange({ myReaction: nextType, reactionCounts: nextCounts, total: nextTotal });
    try {
      const { data } = await api.post(`/posts/${postId}/react`, { type });
      onChange({ myReaction: data.myReaction, reactionCounts: data.reactionCounts, total: data.totalReactions });
    } catch {
      onChange({ myReaction, reactionCounts, total }); // revert
    }
  };

  // Which icons to show as the summary (top 3 present reactions).
  const present = REACTIONS.filter(r => (reactionCounts?.[r.type] || 0) > 0).slice(0, 3);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}
      onMouseEnter={() => { clearTimeout(timerRef.current); timerRef.current = setTimeout(() => setOpen(true), 320); }}
      onMouseLeave={() => { clearTimeout(timerRef.current); }}>
      {open && (
        <div style={{ position: 'absolute', bottom: '100%', left: 0, marginBottom: 6, display: 'flex', gap: 2, background: 'var(--white)', border: '1px solid var(--nat-d)', borderRadius: 999, padding: '5px 7px', boxShadow: '0 8px 28px rgba(0,0,0,0.16)', zIndex: 60 }}>
          {REACTIONS.map(r => (
            <button key={r.type} title={r.label} onClick={() => react(r.type)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 5, borderRadius: '50%', color: r.color, display: 'flex', transition: 'transform 0.12s' }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.25)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
              <r.Icon size={22} filled />
            </button>
          ))}
        </div>
      )}
      <button
        onClick={() => react(myReaction || 'like')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color, padding: '7px 12px', borderRadius: 10, transition: 'background 0.15s' }}
        onMouseEnter={e => e.currentTarget.style.background = 'var(--nat-s)'}
        onMouseLeave={e => e.currentTarget.style.background = 'none'}>
        {present.length > 0
          ? <span style={{ display: 'flex', alignItems: 'center' }}>{present.map((r, i) => <span key={r.type} style={{ color: r.color, marginLeft: i ? -4 : 0, display: 'flex' }}><r.Icon size={15} filled /></span>)}</span>
          : <Icon size={16} filled={!!current} />}
        <span>{current ? current.label : (total > 0 ? total : 'React')}</span>
        {current && total > 1 && <span style={{ color: 'var(--txt-l)', fontWeight: 600 }}>{total}</span>}
      </button>
    </div>
  );
}
